import React from 'react';
import { useAuth } from '../../context/AuthContext';
import { useAuthorization } from '../../context/AuthorizationContext';
import { Alert, Box, Skeleton } from '@mui/material';

interface ProtectedComponentProps {
  children: React.ReactNode;
  // Legacy props for backwards compatibility
  requiredPermission?: string;
  requiredPermissions?: string[];
  requiredRole?: string | string[];
  requireAll?: boolean;
  // New enhanced props
  permissions?: string | string[];
  mode?: 'any' | 'all';
  fallback?: React.ReactNode;
  showFallback?: boolean;
  loading?: React.ReactNode;
  showError?: boolean;
  errorMessage?: string;
  requireOwnership?: boolean;
  resourceType?: string;
  resourceId?: string | number;
}

const ProtectedComponent = ({
  children,
  // Legacy props
  requiredPermission,
  requiredPermissions,
  requiredRole,
  requireAll = false,
  // New props
  permissions,
  mode = 'any',
  fallback,
  showFallback = false,
  loading = <Skeleton variant="rectangular" height={40} />,
  showError = false,
  errorMessage = 'You do not have permission to view this content.',
  requireOwnership = false,
  resourceType,
  resourceId
}: ProtectedComponentProps): React.ReactElement | null => {
  const { user, hasRole } = useAuth();
  const { hasPermission, isLoading, checkResourceOwnership } = useAuthorization();
  const [ownershipChecked, setOwnershipChecked] = React.useState(!requireOwnership);
  const [hasOwnership, setHasOwnership] = React.useState(false);

  // Check resource ownership if required
  React.useEffect(() => {
    if (requireOwnership && resourceType && resourceId) {
      checkResourceOwnership(resourceType, resourceId).then(result => {
        setHasOwnership(result);
        setOwnershipChecked(true);
      });
    }
  }, [requireOwnership, resourceType, resourceId, checkResourceOwnership]);

  // Show loading state
  if (isLoading || !ownershipChecked) {
    return <>{loading}</>;
  }

  // If user is not authenticated, don't show anything
  if (!user) {
    if (showFallback) {
      return (
        fallback ? (
          <>{fallback}</>
        ) : (
          <Alert severity="warning">
            You must be logged in to access this content.
          </Alert>
        )
      ) as React.ReactElement;
    }
    return null;
  }

  // Check role requirements (legacy support)
  if (requiredRole && !hasRole(requiredRole)) {
    if (showFallback) {
      return (
        fallback ? (
          <>{fallback}</>
        ) : (
          <Alert severity="error">
            You don't have the required role to access this content.
          </Alert>
        )
      ) as React.ReactElement;
    }
    return null;
  }

  // Determine which permissions to check (new system takes precedence)
  let permissionsToCheck: string | string[] | undefined;
  let checkMode: 'any' | 'all' = 'any';

  if (permissions) {
    permissionsToCheck = permissions;
    checkMode = mode;
  } else if (requiredPermission) {
    permissionsToCheck = requiredPermission;
  } else if (requiredPermissions && requiredPermissions.length > 0) {
    permissionsToCheck = requiredPermissions;
    checkMode = requireAll ? 'all' : 'any';
  }

  // Check permissions
  let hasRequiredPermissions = true;
  if (permissionsToCheck) {
    hasRequiredPermissions = hasPermission(permissionsToCheck, checkMode);
  }

  // Check ownership if required
  const hasRequiredOwnership = !requireOwnership || hasOwnership;

  const hasAccess = hasRequiredPermissions && hasRequiredOwnership;

  if (!hasAccess) {
    if (showFallback || showError) {
      return (
        fallback ? (
          <>{fallback}</>
        ) : (
          <Alert severity="error">
            {errorMessage}
          </Alert>
        )
      ) as React.ReactElement;
    }
    return null;
  }

  return <>{children}</> as React.ReactElement;
};

export default ProtectedComponent;